/**
 * Currency ↔ Country Sync
 * Keeps the Bucks Currency Converter (by Helixo) in sync with
 * Shopify's native country/market selector.
 *
 * Key findings from live-site inspection:
 * - Bucks does NOT reload the page; it converts prices client-side.
 * - Bucks does NOT fire a native "change" event on #bucksSelector.
 * - Bucks uses custom <li> elements (.buckscc-select-options li) for its dropdown.
 * - Bucks stores manual selection in localStorage as:
 *     buckscc_last_manual = {"currency":"USD","timestamp":1234567890}
 * - Bucks' primary currency source is sessionStorage:
 *     buckscc_customer_currency = "USD" (JSON-encoded string)
 * - Shopify's localization form calls form.submit() directly, which does NOT
 *   fire the "submit" event — so we listen for clicks on country links instead.
 */
(function () {
  'use strict';

  var currencyToCountry = { USD: 'US', AED: 'AE', INR: 'IN' };
  var countryToCurrency = { US: 'USD', AE: 'AED', IN: 'INR' };

  var BUCKS_MANUAL_KEY = 'buckscc_last_manual';
  var BUCKS_CUSTOMER_KEY = 'buckscc_customer_currency';
  var RELOAD_LOG_KEY = '_ccsync_reloads';
  var MAX_RELOADS = 3;
  var RELOAD_WINDOW = 8000;

  /* ── Cascade protection ─────────────────────────────────── */
  function logReload() {
    var now = Date.now();
    var log;
    try { log = JSON.parse(sessionStorage.getItem(RELOAD_LOG_KEY)) || []; } catch (e) { log = []; }
    log.push(now);
    log = log.filter(function (t) { return now - t < RELOAD_WINDOW; });
    sessionStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(log));
    return log.length;
  }

  function isCascading() {
    var log;
    try { log = JSON.parse(sessionStorage.getItem(RELOAD_LOG_KEY)) || []; } catch (e) { log = []; }
    var now = Date.now();
    return log.filter(function (t) { return now - t < RELOAD_WINDOW; }).length >= MAX_RELOADS;
  }

  if (isCascading()) {
    sessionStorage.removeItem(RELOAD_LOG_KEY);
    return;
  }

  /* ── Helper: submit a native country form ───────────────── */
  function submitCountryForm(countryCode) {
    var formIds = [
      'HeaderCountryForm',
      'FooterCountryForm',
      'HeaderCountryMobileForm',
      'AnnouncementCountryForm'
    ];
    for (var i = 0; i < formIds.length; i++) {
      var form = document.getElementById(formIds[i]);
      if (!form) continue;
      var input = form.querySelector('input[name="country_code"]');
      if (!input) continue;
      if (input.value === countryCode) return; // already correct
      input.value = countryCode;
      logReload();
      form.submit();
      return;
    }
  }

  /* ── Helper: set Bucks currency in storage ────────────────
   * Bucks reads from sessionStorage (buckscc_customer_currency)
   * as its primary source, and localStorage (buckscc_last_manual)
   * as a fallback. We set both to ensure it picks up the change.
   */
  function setBucksCurrency(currencyCode) {
    try {
      sessionStorage.setItem(BUCKS_CUSTOMER_KEY, JSON.stringify(currencyCode));
      localStorage.setItem(BUCKS_MANUAL_KEY, JSON.stringify({
        currency: currencyCode,
        timestamp: Date.now()
      }));
    } catch (e) { /* noop */ }
  }

  /* ── A. Bucks → Country sync ────────────────────────────── */
  // Listen for clicks on Bucks' custom <li> dropdown items.
  // After Bucks handles the click (client-side conversion),
  // we submit the Shopify country form to switch markets.
  function attachBucksListeners(container) {
    var items = container.querySelectorAll('li.converterTriggers');
    items.forEach(function (li) {
      li.addEventListener('click', function () {
        var currency = li.getAttribute('rel') || li.id;
        if (!currency) return;
        var country = currencyToCountry[currency];
        if (!country) return; // e.g. CAD — no matching market

        // Delay slightly so Bucks finishes its own handling first
        setTimeout(function () {
          submitCountryForm(country);
        }, 100);
      });
    });
  }

  function initBucksSync() {
    var optionsList = document.querySelector('.buckscc-select-options');
    if (optionsList) {
      attachBucksListeners(optionsList);
    } else {
      // Bucks may inject the widget after our script runs
      var observer = new MutationObserver(function () {
        var el = document.querySelector('.buckscc-select-options');
        if (el) {
          observer.disconnect();
          attachBucksListeners(el);
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  /* ── B. Country → Bucks sync ────────────────────────────── */
  // Listen for clicks on country selector links (a[data-value]).
  // form.submit() doesn't fire "submit" events, so we intercept
  // clicks in capture phase before the localization-form handler
  // calls form.submit(). We set Bucks' localStorage so after the
  // reload, Bucks displays the matching currency.
  function attachCountryClickListeners() {
    var countryFormIds = [
      'HeaderCountryForm',
      'FooterCountryForm',
      'HeaderCountryMobileForm',
      'AnnouncementCountryForm'
    ];
    countryFormIds.forEach(function (id) {
      var form = document.getElementById(id);
      if (!form) return;
      // The localization-form custom element wraps the form
      var wrapper = form.closest('localization-form') || form.parentElement;
      wrapper.addEventListener('click', function (e) {
        var link = e.target.closest('a[data-value]');
        if (!link) return;
        var countryCode = link.getAttribute('data-value');
        var currency = countryToCurrency[countryCode];
        if (!currency) return;
        setBucksCurrency(currency);
        logReload();
      }, true); // capture phase — runs before the form submits
    });
  }

  /* ── Init ────────────────────────────────────────────────── */
  function init() {
    initBucksSync();
    attachCountryClickListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
